import React, { useState, useEffect } from 'react';
import { Loader2, Lightbulb } from 'lucide-react';
import { getCollegeTriviaFacts } from '../../../utils/collegeTriviaFallback';

/**
 * TriviaLoader - Shows rotating trivia facts while questions are being generated
 *
 * @param {Object} college - College object (may include trivia_facts)
 * @param {string} schoolName - Name of the school for display
 * @param {string} message - Main loading message
 */
export default function TriviaLoader({
  college = null,
  schoolName = 'this college',
  message = 'Generating personalized questions...'
}) {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [triviaFacts, setTriviaFacts] = useState([]);

  // Load trivia facts when component mounts
  useEffect(() => {
    const facts = getCollegeTriviaFacts(college);
    // Shuffle facts for variety
    const shuffled = [...facts].sort(() => Math.random() - 0.5);
    setTriviaFacts(shuffled);
  }, [college]);

  // Rotate through facts every 4 seconds
  useEffect(() => {
    if (triviaFacts.length === 0) return;

    const interval = setInterval(() => {
      setCurrentFactIndex((prev) => (prev + 1) % triviaFacts.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [triviaFacts]);

  if (triviaFacts.length === 0) {
    // Fallback while loading trivia
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-violet-500 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">{message}</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-8 max-w-lg w-full shadow-xl">
        <div className="text-center">
          {/* Spinner */}
          <div className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-violet-500 rounded-xl flex items-center justify-center mx-auto mb-4 relative">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          </div>

          {/* Main message */}
          <h3 className="text-xl font-bold text-slate-900 mb-2">{message}</h3>
          <p className="text-sm text-slate-500 mb-6">
            This usually takes 10-15 seconds...
          </p>

          {/* Trivia section */}
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-xl p-6 mt-6">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Lightbulb className="w-5 h-5 text-amber-600" />
              <h4 className="text-sm font-bold text-amber-900 uppercase tracking-wide">
                {college?.trivia_facts ? `About ${schoolName}` : 'College Trivia'}
              </h4>
            </div>

            {/* Trivia fact with fade animation */}
            <div className="relative min-h-[60px] flex items-center justify-center">
              <p
                key={currentFactIndex}
                className="text-slate-700 leading-relaxed animate-fade-in"
              >
                {triviaFacts[currentFactIndex]}
              </p>
            </div>

            {/* Progress dots */}
            <div className="flex items-center justify-center gap-1.5 mt-4">
              {triviaFacts.slice(0, 5).map((_, index) => (
                <div
                  key={index}
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                    index === currentFactIndex % 5
                      ? 'bg-amber-600 w-4'
                      : 'bg-amber-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Add fade-in animation */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}
